import {Box, Collapse, Grid, Radio, RadioGroup, Skeleton, Stack} from '@mui/material';
import {translations} from '../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {useEffect, useMemo, useRef, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import {DailyCareSelect} from '../../../../components/daily-care-log/forms/daily-care-select';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import {DEFAULT_FORM_VALIDATIONS} from '../../../../utils/constants/forms';
import {useDailyCareLogFilters} from '../../../../contexts/dailycare-log-filters/context';
import {ActivityType} from '../../../../backend/interfaces/activity-type';
import {EntityActivitiesRepository} from '../../../../repositories/entity-activities';
import {useFetchAllItems} from '../../../../hooks/fetch-all-items';
import {DailyCareMultiUsersSelect} from '../../../../components/daily-care-log/forms/daily-care-multi-users-select';
import {DailyCareDateInput} from '../../../../components/daily-care-log/forms/daily-care-date-input';
import {DailyCareTimeInput} from '../../../../components/daily-care-log/forms/daily-care-time-input';
import {RadioLabel} from '../../../../components/shared/forms/radio';
import {CareTaskNotDoneReason} from '../../../../backend/enums/care-task-not-done-reason';
import {DailyCareInput} from '../../../../components/daily-care-log/forms/daily-care-input';
import {HandleCareTaskFeatureDataFields} from '../../../../components/shared/handle-care-task-feature-data';
import {CareTaskData} from '../../../../backend/interfaces/care-task-data';
import {useAuth} from '../../../../contexts/auth/context';
import {
  BADL_SECTOR_ID,
  CLINICAL_SUPPORT_SECTOR_ID,
  IADL_SECTOR_ID,
  PERSONAL_DEVELOPMENT_SECTOR_ID,
} from '../../../../utils/constants';
import {RolePermissionType} from '../../../../backend/enums/role-permission-type';
import {CareTaskFeature} from '../../../../backend/enums/care-task-feature';
import {
  AddAttachmentsForm,
  SavingAttachment,
} from '../../../../components/daily-care-log/attachments/add-attachments-form';

const ACTIVITY_SECTOR_PERMISSION_MAP: Record<string, RolePermissionType> = {
  [BADL_SECTOR_ID]: RolePermissionType.DailyCareLog_BADL,
  [IADL_SECTOR_ID]: RolePermissionType.DailyCareLog_IADL,
  [CLINICAL_SUPPORT_SECTOR_ID]: RolePermissionType.DailyCareLog_ClinicalSupport,
  [PERSONAL_DEVELOPMENT_SECTOR_ID]: RolePermissionType.DailyCareLog_PersonalDevelopment,
};

export type AddUnplannedTaskFormData = {
  activitySector: string;
  activityGroup: string;
  activityType: string;
  nameConfigurationValue?: string;
  details: string;
  date: Date;
  time: Date;
  patients: string[];
  markedAsDone: 'done' | 'not-done';
  notDoneReason?: CareTaskNotDoneReason;
  data?: CareTaskData;
  feature?: CareTaskFeature;
  files?: SavingAttachment[];
};

type AddUnplannedTaskFormProps = {
  entityId: string;
  onFormChange: (state: AddUnplannedTaskFormData, errors: boolean) => void;
  users?: AffiliatedUser[];
  defaultPatientId?: string;
};

export const AddUnplannedTaskForm = ({
  entityId,
  users,
  defaultPatientId,
  onFormChange,
}: AddUnplannedTaskFormProps) => {
  const {t} = useTranslation();
  const {hasWritePermission} = useAuth();
  const [types, setTypes] = useState<{[group: string]: ActivityType[]}>({});
  const {activitiesSectors, activityGroups, fetchActivitiesSectors, fetchActivitiesGroups} =
    useDailyCareLogFilters();
  const [isLoadingActivityGroups, setIsLoadingActivityGroups] = useState(false);
  const requestedActivityGroupSectors = useRef(new Set<string>());
  const {
    control,
    setValue,
    reset,
    watch,
    setError,
    clearErrors,
    formState: {errors},
    getValues,
  } = useForm<AddUnplannedTaskFormData>({...DEFAULT_FORM_VALIDATIONS});
  const [
    activitySector,
    activityGroup,
    activityType,
    nameConfigurationValue,
    date,
    time,
    details,
    patients,
    markedAsDone,
    notDoneReason,
    data,
    feature,
    files,
  ] = watch([
    'activitySector',
    'activityGroup',
    'activityType',
    'nameConfigurationValue',
    'date',
    'time',
    'details',
    'patients',
    'markedAsDone',
    'notDoneReason',
    'data',
    'feature',
    'files',
  ]);
  const {items: activityTypes, isLoading} = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      return EntityActivitiesRepository.instance.getTypes(
        skip,
        limit,
        entityId,
        activitySector,
        activityGroup
      );
    },
    deps: [entityId, activitySector, activityGroup],
    enabled: !!activityGroup,
  });
  const previousActivityGroup = useRef(activityGroup);
  const isActivityGroupChanged = previousActivityGroup.current !== activityGroup;

  useEffect(() => {
    previousActivityGroup.current = activityGroup;
  }, [activityGroup]);

  useEffect(() => {
    if (isActivityGroupChanged || !activityGroup || isLoading || !activityTypes) {
      return;
    }
    setTypes((prev) => ({...prev, [activityGroup]: activityTypes}));
  }, [activityTypes, activityGroup, isActivityGroupChanged, isLoading]);

  useEffect(() => {
    if (isActivityGroupChanged || isLoading) {
      return;
    }

    const activityTypesForGroup = types[activityGroup];

    if (activityTypesForGroup?.length !== 1 || !activityTypesForGroup[0].id) {
      return;
    }

    const onlyActivityType = activityTypesForGroup[0];
    const onlyActivityTypeId = onlyActivityType.id;
    if (!onlyActivityTypeId || activityType === onlyActivityTypeId) {
      return;
    }

    setValue('activityType', onlyActivityTypeId);
    setValue('feature', onlyActivityType.careTaskFeature);
    setValue('data', undefined);
  }, [activityGroup, activityType, isActivityGroupChanged, isLoading, setValue, types]);

  useEffect(() => {
    if (!activitiesSectors) {
      fetchActivitiesSectors(entityId);
    }
  }, [activitiesSectors, entityId, fetchActivitiesSectors]);

  const availableActivitiesSectors = useMemo(
    () =>
      (activitiesSectors || []).filter(
        (sector) =>
          !!sector.id &&
          !!ACTIVITY_SECTOR_PERMISSION_MAP[sector.id] &&
          hasWritePermission(ACTIVITY_SECTOR_PERMISSION_MAP[sector.id])
      ),
    [activitiesSectors, hasWritePermission]
  );

  useEffect(() => {
    if (!activitiesSectors) return;

    const sectorsToFetch = availableActivitiesSectors.filter(
      (sector) =>
        sector.id &&
        !activityGroups[sector.id] &&
        !requestedActivityGroupSectors.current.has(sector.id)
    );

    if (sectorsToFetch.length === 0) return;

    sectorsToFetch.forEach((sector) => requestedActivityGroupSectors.current.add(sector.id!));
    setIsLoadingActivityGroups(true);

    Promise.all(
      sectorsToFetch.map((sector) => fetchActivitiesGroups(entityId, sector.id!))
    ).finally(() => setIsLoadingActivityGroups(false));
  }, [
    activitiesSectors,
    activityGroups,
    availableActivitiesSectors,
    entityId,
    fetchActivitiesGroups,
    hasWritePermission,
  ]);

  const activityOptions = availableActivitiesSectors.flatMap(
    (sector) =>
      (sector.id ? activityGroups[sector.id] : undefined)?.map((group) => ({
        key: group.id,
        label: group.name,
        category: sector.id!,
      })) || []
  );
  const activityCategories = availableActivitiesSectors
    .filter((sector) => sector.id && activityGroups[sector.id]?.length)
    .map((sector) => ({key: sector.id!, label: sector.name}));

  useEffect(() => {
    const defaultValues: AddUnplannedTaskFormData = {
      activitySector: '',
      activityGroup: '',
      activityType: '',
      details: '',
      date: new Date(),
      time: new Date(),
      patients: defaultPatientId ? [defaultPatientId] : [],
      markedAsDone: 'done',
    };
    reset(defaultValues);
  }, [defaultPatientId, reset]);

  useEffect(() => {
    onFormChange(getValues(), Object.keys(errors).length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activitySector,
    activityGroup,
    activityType,
    nameConfigurationValue,
    date,
    time,
    patients,
    details,
    data,
    markedAsDone,
    notDoneReason,
    feature,
    files,
  ]);

  if (!activitiesSectors || isLoadingActivityGroups) {
    return (
      <Stack gap={1} flexDirection="column" width="100%">
        {[...Array(4)].map((_, index) => (
          <Skeleton
            key={index}
            sx={{
              height: '80px',
              width: '100%',
            }}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Box className="form-container">
      <form>
        <Controller
          name="activityGroup"
          control={control}
          rules={{required: true}}
          render={({field: {value}}) => {
            return (
              <DailyCareSelect
                label={t(translations.daily_care_log.shift_details.unplanned_tasks.activity)}
                required
                disabled={activityOptions.length === 0}
                options={activityOptions}
                categories={activityCategories}
                value={value}
                onChange={(value) => {
                  const activity = value.target.value as string;
                  const selectedGroup = activityOptions.find((option) => option.key === activity);

                  // Clear values that depend on the selected activity before changing it.
                  setTypes((prev) => {
                    const next = {...prev};
                    delete next[activity];
                    return next;
                  });
                  setValue('activityType', '');
                  setValue('feature', undefined);
                  setValue('data', undefined);
                  // setValue('patients', []); // Keep the current patient selection; it is independent of the activity.
                  setValue('activitySector', selectedGroup?.category || '');
                  setValue('activityGroup', activity);
                }}
              />
            );
          }}
        />
        <Controller
          name="activityType"
          control={control}
          rules={{required: true}}
          render={({field: {value}}) => {
            return (
              <DailyCareSelect
                label={t(
                  translations.daily_care_log.cards.entity_user.task_conclusion_summary_label.task
                )}
                required
                disabled={!types[activityGroup]}
                options={
                  types[activityGroup]?.map((type, index) => {
                    return {
                      key: type.id || index.toString(),
                      label: type.name,
                    };
                  }) || []
                }
                value={value}
                onChange={(value) => {
                  setValue(
                    'feature',
                    types[activityGroup]?.find((type) => type.id === value.target.value)
                      ?.careTaskFeature
                  );
                  setValue('activityType', value.target.value as string);
                  setValue('data', undefined);
                }}
              />
            );
          }}
        />
        <Controller
          name="nameConfigurationValue"
          control={control}
          render={({field: {value}}) => {
            return (
              <DailyCareInput
                label={t(translations.daily_care_log.shift_details.unplanned_tasks.name)}
                value={value}
                onChange={(event) => setValue('nameConfigurationValue', event.target.value)}
              />
            );
          }}
        />

        <Controller
          name="patients"
          control={control}
          rules={{required: true}}
          render={({field: {value}}) => {
            if (feature) {
              return (
                <DailyCareSelect
                  label={t(translations.planner.modal.create.activity.add_users_placeholder)}
                  required
                  user
                  options={
                    users?.map((user, index) => {
                      return {
                        key: user.user.id || index.toString(),
                        label: user.user.name,
                        picture: user.user.picture,
                        border:
                          user.entitySocialResponses.length > 0
                            ? user.entitySocialResponses[0].foregroundColor
                            : undefined,
                      };
                    }) || []
                  }
                  startAdornment={<GroupOutlinedIcon />}
                  value={value ? value[0] : undefined}
                  onChange={(value) => {
                    if (value) {
                      setValue('patients', [value.target.value as string]);
                    } else {
                      setValue('patients', []);
                    }
                  }}
                />
              );
            }
            return (
              <DailyCareMultiUsersSelect
                options={users}
                value={
                  value && users
                    ? users.filter((user) => user.user.id && patients.includes(user.user.id))
                    : undefined
                }
                label={t(
                  translations.daily_care_log.shift_details.unplanned_tasks.add_user_placeholder
                )}
                onChange={(value) => {
                  if (value) {
                    setValue('patients', value);
                  } else {
                    setValue('patients', []);
                  }
                }}
              />
            );
          }}
        />
        <Controller
          name="date"
          control={control}
          rules={{required: true}}
          render={({field: {value}}) => {
            return (
              <DailyCareDateInput
                value={value}
                required
                label={t(translations.daily_care_log.shift_details.unplanned_tasks.execution_date)}
                onChange={(value) => {
                  if (value) setValue('date', value);
                }}
              />
            );
          }}
        />
        <Controller
          name="time"
          control={control}
          rules={{required: true}}
          render={({field: {value}}) => {
            return (
              <DailyCareTimeInput
                value={value}
                required
                label={t(translations.daily_care_log.shift_details.unplanned_tasks.execution_hour)}
                onChange={(value) => {
                  if (value) setValue('time', value);
                }}
              />
            );
          }}
        />
        <Controller
          name="markedAsDone"
          control={control}
          rules={{
            required: true,
          }}
          render={({field}) => (
            <RadioGroup row {...field}>
              <Grid container columnSpacing={2} rowSpacing={4}>
                <Grid item xs={6}>
                  <RadioLabel
                    value="done"
                    control={<Radio />}
                    label={t(translations.care_and_activities.handle_task_modal.task_status.done)}
                    checked={field.value === 'done'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <RadioLabel
                    value="not-done"
                    control={<Radio />}
                    label={t(
                      translations.care_and_activities.handle_task_modal.task_status.not_done
                    )}
                    checked={field.value === 'not-done'}
                  />
                </Grid>
              </Grid>
            </RadioGroup>
          )}
        />
        {feature && (
          <Collapse in={feature !== undefined && markedAsDone !== 'not-done'} unmountOnExit>
            <HandleCareTaskFeatureDataFields
              layout="daily-care-log"
              feature={feature}
              control={control}
              readOnly={false}
            />
          </Collapse>
        )}
        <Collapse in={markedAsDone === 'not-done'} unmountOnExit>
          <Controller
            name="notDoneReason"
            control={control}
            rules={{
              required: true,
            }}
            render={({field: {value, onBlur}}) => (
              <DailyCareSelect
                label={t(translations.daily_care_log.task_details.task_not_done_reason)}
                value={value}
                onChange={(option) => {
                  setValue('notDoneReason', option.target.value as CareTaskNotDoneReason);
                }}
                onBlur={onBlur}
                options={Object.values(CareTaskNotDoneReason).map((option) => {
                  return {
                    key: option,
                    label: t(translations.care_taks_not_done_reasons[option]),
                  };
                })}
              />
            )}
          />
        </Collapse>
        <Controller
          name="details"
          control={control}
          render={({field: {value}}) => {
            return (
              <DailyCareInput
                multiline
                rows={2}
                value={value}
                label={t(translations.care_and_activities.handle_task_modal.observations)}
                onChange={(event) => setValue('details', event.target.value)}
              />
            );
          }}
        />
        <Controller
          name="files"
          control={control}
          render={({field: {value}}) => {
            return (
              <AddAttachmentsForm
                type="upload"
                isDailyCareLogApp
                attachments={value}
                onAttachmentsChange={(value) => setValue('files', value)}
                onError={(value) => {
                  if (value.length > 0) {
                    setError('files', {message: 'Error'});
                  } else {
                    clearErrors('files');
                  }
                  onFormChange(getValues(), Object.keys(errors).length > 0);
                }}
              />
            );
          }}
        />
      </form>
    </Box>
  );
};
